//@author: devin
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    input,
    OnChanges,
    OnInit,
    SimpleChanges,
    TemplateRef,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25DropdownPaginatedComponent } from "../s25.dropdown.paginated.component";
import { GenericDropdownUtil } from "./generic.dropdown.util";
import { DropDownPaginatedModel } from "../../../pojo/DropDownPaginatedModel";
import { S25DropdownAbstract } from "../s25.dropdown.abstract";

@TypeManagerDecorator("s25-generic-dropdown")
@Component({
    selector: "s25-generic-dropdown",
    template: `
        @if (items) {
            <s25-dropdown-paginated
                [choice]="choice || defaultChoice"
                [model]="model"
                [onSelect]="onSelect"
                [chosen]="value()"
                [onLoad]="onLoad"
                [fieldId]="fieldId()"
                [ariaLabel]="ariaLabel || placeholder"
                [filter]="filter"
                [renderItem]="renderItem"
                [hasNoneItem]="hasNoneItem"
                [noneItemLabel]="noneItemLabel"
                [scrollIntoView]="scrollIntoView"
                [readOnly]="readOnly"
                [blockDetailsNav]="blockDetailsNav"
                (chosenChange)="onValueChange($event)"
            />
        }

        <ng-template #defaultChoice let-item="item" let-$transcludeCtrl="$transcludeCtrl" let-chosen="chosen">
            <div [class.chosen]="chosen">
                @if (
                    $transcludeCtrl.isLoggedIn && $transcludeCtrl.model.hasFav && item.itemId && isNumeric(item.itemId)
                ) {
                    <s25-favorite
                        #fav
                        role="presentation"
                        [attr.aria-label]="fav.isFav() ? 'Favorite: ' : 'Not in favorites: '"
                        [item]="item"
                    >
                        <span class="ngFloatRight ngClearBoth ngSearchSelectFavorite"></span>
                    </s25-favorite>
                }
                <span [innerHTML]="item.txt"></span>
                @if (item.itemDesc && !chosen) {
                    <span>&nbsp;({{ item.itemDesc }})</span>
                }
            </div>
        </ng-template>
    `,
    styles: `
        s25-generic-dropdown .chosen {
            display: flex;
            gap: 0.5em;
        }
        s25-generic-dropdown .chosen s25-favorite {
            align-self: center;
        }
        s25-dropdown-paginated .s25-item-holder .s25-item-name {
            line-height: 2em;
        }
        .ngStarOn {
            margin-left: 0.6em;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25GenericDropdownComponent<T extends string | number>
    extends S25DropdownAbstract<T>
    implements OnInit, OnChanges
{
    @Input() items: DropDownItem<T>[];
    @Input() placeholder: string;
    @Input() searchPlaceholder: string;
    @Input() popoverClass?: string;
    @Input() choice: TemplateRef<any>;
    @Input() useChoiceForChosen?: boolean;
    @Input() groupProp: string = "grp";
    @Input() renderItem: boolean = true;
    readonly fieldId = input<string>();

    readonly dropdown = viewChild(S25DropdownPaginatedComponent);

    constructor(private changeDetector: ChangeDetectorRef) {
        super();
    }

    origItems: DropDownItem<T>[];
    model: DropDownPaginatedModel;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.items && !changes.items.firstChange) {
            this.ngOnInit();
        }
    }

    ngOnInit() {
        const value = this.value();
        if (this.items && !this.items.length && value) {
            this.items.push(value); //add value() to items list if no items present so it appears as selected
        }

        this.origItems = S25Util.deepCopy(this.items);

        //if custom filter and items, pre-filter them based on custom filter
        if (this.apiBean && this.apiBean.customFilter && this.items && this.items.length) {
            this.items = this.items.filter((item: DropDownItem<T>) => this.apiBean.customFilter(item));
        }

        value && GenericDropdownUtil.extractItems([value]);
        this.model = {
            hasFav: S25Util.toBool(this.hasFav),
            staticItems: GenericDropdownUtil.extractItems(this.staticItems),
            items: GenericDropdownUtil.extractItems(this.items),
            itemNameProp: "txt",
            groupNameProp: this.groupProp,
            serverSide: this.serverSide,
            placeholder: this.placeholder || "Select an Item",
            searchPlaceholder: this.searchPlaceholder,
            emptyText: this.emptyText || "No Items Available",
            searchEnabled: S25Util.toBool(this.searchEnabled),
            autoOpen: S25Util.toBool(this.autoOpen),
            focusFirst: S25Util.toBool(this.focusFirst),
            alwaysOpen: S25Util.toBool(this.alwaysOpen),
            hideSearch: S25Util.toBool(this.hideSearch),
            resetSelectedOnCleanup: S25Util.toBool(this.resetSelectedOnCleanup),
            popoverClass: this.popoverClass || "",
            useChoiceForChosen: this.useChoiceForChosen,
            noSelection: this.noSelection,
        };

        this.apiBean = this.apiBean || {};
        S25Util.extend(this.apiBean, {
            getItems: () => {
                return this.model.items || [];
            },
            getOrigItems: () => {
                return this.origItems;
            }, //return orig items to caller
            getFirstElementId: () => {
                //exposed via dropdown-paginated
                return this.model.getFirstElementId();
            },
            getHighlightedItem: () => {
                //exposed via dropdown-paginated
                return this.model.getHighlightedItem();
            },
        });
    }

    focusDropdown() {
        this.dropdown()?.focusDropdown();
    }

    isNumeric(itemId: any) {
        return S25Util.isNumeric(itemId);
    }
}
